// src/components/Menu/MenuSide.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { BASE_URL } from "../../Utils/api";
import {
  FiHome, FiFolder, FiSettings, FiChevronRight, FiChevronDown, FiChevronLeft,
  FiUser, FiUsers, FiBriefcase, FiShoppingBag, FiTruck, FiFileText,
  FiPercent, FiFile, FiGrid, FiMail, FiMenu, FiX, FiCalendar, FiClock, FiBarChart2
} from "react-icons/fi";
import "./MenuSide.css";
import { GlobalContext } from "../../Utils/GlobalContext";
import { getPermFlags } from "../../Utils/controlaPermissao";
import NovidadesVersaoModal from "../Login/NovidadesVersaoModal";
import MenuLogoUploadModal from "./MenuLogoUploadModal";


const MenuSide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const [showNovidadesModal, setShowNovidadesModal] = useState(false);
  const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);

  // permissões
  const { globalData, setGlobalData } = useContext(GlobalContext);
  const {
    permiteTiposAgendamento,
    permiteDocumentos,
    permiteConfiguracoesCampos,
    permiteConfiguracoesEmail,
    constPermiteEmpresas,
    constPermiteEntidades,
    constPermiteFornecedores,
    constPermiteFuncionarios,
    constPermiteGerarAgendamentos,
    constPermiteGruposUsuarios,
    constPermiteIndicesCorrecao,
    constPermiteTiposDocumento,
    constPermiteTiposFornecedor,
    constPermiteTiposdePagamento,
    constPermiteUsuarios,
    constPermiteCamposCustomizados,
  } = getPermFlags(globalData);

  const temNovidades = Boolean(globalData?.temNovidades && Array.isArray(globalData?.novidadesVersao) && globalData.novidadesVersao.length > 0);
  const novidadesVersao = globalData?.novidadesVersao || [];
  const idEntidade = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const logoValor = typeof globalData?.logotipo === "string" ? globalData.logotipo : "";
  const logoSrc = logoValor
    ? /^(data:|blob:|https?:\/\/|\/)/i.test(logoValor)
      ? logoValor
      : `data:image/png;base64,${logoValor}`
    : null;

  useEffect(() => {
    if (temNovidades) setShowNovidadesModal(true);
  }, [temNovidades]);

  useEffect(() => {
    document.body.classList.remove("login-page-body");
    const savedCollapsed = localStorage.getItem("sidebarCollapsed");
    if (savedCollapsed) setCollapsed(JSON.parse(savedCollapsed));
    setMobileOpen(false);
    updateActiveMenuFromPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const updateActiveMenuFromPath = () => {
    const path = location.pathname.substring(1);
    if (path === "PaginaInicial" || path === "") {
      setActiveMenu("");
    } else if (path === "Estatisticas" || path === "PainelConsolidadoDocumentos") {
      setActiveMenu("painelGestao");
    } else if (
      path === "Empresas" ||
      path === "Usuario" ||
      path === "TiposFornecedor" ||
      path === "TiposDocumento" ||
      path === "ClassificacaoTiposDocumento" ||
      path === "GrupoUsuario" ||
      path === "IndicesCorrecao" ||
      path === "TiposPagamento" ||
      path === "Documentos" ||
      path === "Funcionarios"
    ) {
      setActiveMenu("cadastros");
    } else if (
      path === "ConfiguracoesCampos" ||
      path === "EmailNotificacao" ||
      path === "TiposAgendamento" ||
      path === "Agendamentos" ||
      path === "CamposCustomizados"
    ) {
      setActiveMenu("configuracoes");
    }
  };

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed));
    if (collapsed) document.body.classList.add("sidebar-collapsed");
    else document.body.classList.remove("sidebar-collapsed");
  }, [collapsed]);

  // 🔹 agora seta via contexto
  const handleNavigation = (route, classification) => {
    if (classification) {
      setGlobalData((prev) => ({ ...prev, classificacao: classification }));
    }
    navigate(route);
    setMobileOpen(false);
  };

  const handleLogoClick = () => {
    if (logoSrc) {
      navigate("/Estatisticas");
      setMobileOpen(false);
      return;
    }
    setShowLogoUploadModal(true);
  };

  const handleLogoSaved = (logotipo) => {
    setGlobalData((prev) => ({ ...prev, logotipo }));
  };

  const toggleSidebar = () => setCollapsed(!collapsed);
  const toggleMobileMenu = () => setMobileOpen(!mobileOpen);
  const toggleSubMenu = (menuName) =>
    setActiveMenu(activeMenu === menuName ? "" : menuName);

  const handleCloseNovidadesModal = async () => {
    const ids = (novidadesVersao || []).map((n) => n.id).filter((id) => id != null);
    if (ids.length > 0) {
      try {
        const token = localStorage.getItem("authToken");
        await api.post(
          `${BASE_URL}/api/versoes/visualizar`,
          { id_versoes: ids },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error("Erro ao marcar novidades como visualizadas:", err);
      }
    }
    setGlobalData((prev) => ({ ...prev, temNovidades: false, novidadesVersao: [] }));
    setShowNovidadesModal(false);
  };

  // 🔹 usa contexto para checar ativo (antes lia localStorage) 
  const isActive = (path, classification) => {
    if (path === "Empresas" && classification) {
      return (
        location.pathname === `/${path}` &&
        globalData?.classificacao === classification
      );
    }
    return location.pathname === `/${path}`;
  };

  const menuItems = [
    {
      id: "painelGestao",
      title: "Painel de Gestão",
      icon: <FiHome />,
      subMenus: [
        {
          title: "Estatísticas",
          icon: <FiBarChart2 />,
          path: "Estatisticas",
          onClick: () => handleNavigation("/Estatisticas"),
        },
        {
          title: "Painel Consolidado de Documentos",
          icon: <FiFile />,
          path: "PainelConsolidadoDocumentos",
          onClick: () => handleNavigation("/PainelConsolidadoDocumentos"),
        },
      ],
    },
    {
      id: "cadastros",
      title: "Cadastros",
      icon: <FiFolder />,
      subMenus: [
        permiteDocumentos && {
          title: "Documentos",
          icon: <FiFile />,
          path: "Documentos",
          onClick: () => handleNavigation("/Documentos"),
        },
        constPermiteEmpresas && {
          title: "Filiais",
          icon: <FiShoppingBag />,
          path: "Empresas",
          classification: "V",
          onClick: () => handleNavigation("/Empresas", "V"),
        },
        false && constPermiteEntidades && {
          title: "Entidades",
          icon: <FiBriefcase />,
          path: "Empresas",
          classification: "E",
          onClick: () => handleNavigation("/Empresas", "E"),
        },
        constPermiteFornecedores && {
          title: "Fornecedores",
          icon: <FiTruck />,
          path: "Empresas",
          classification: "F",
          onClick: () => handleNavigation("/Empresas", "F"),
        },
        constPermiteFuncionarios && {
          title: "Funcionários",
          icon: <FiUser />,
          path: "Funcionarios",
          onClick: () => handleNavigation("/Funcionarios"),
        },
        constPermiteIndicesCorrecao && {
          title: "Índices de Correção",
          icon: <FiPercent />,
          path: "IndicesCorrecao",
          onClick: () => handleNavigation("/IndicesCorrecao"),
        },
        constPermiteTiposDocumento && {
          title: "Tipos Documento",
          icon: <FiFileText />,
          path: "TiposDocumento",
          onClick: () => handleNavigation("/TiposDocumento"),
        },
        constPermiteTiposDocumento && {
          title: "Classificação Tipos de Documento",
          icon: <FiFileText />,
          path: "ClassificacaoTiposDocumento",
          onClick: () => handleNavigation("/ClassificacaoTiposDocumento"),
        },
        constPermiteTiposFornecedor && {
          title: "Tipos de Fornecedor",
          icon: <FiFileText />,
          path: "TiposFornecedor",
          onClick: () => handleNavigation("/TiposFornecedor"),
        },
         constPermiteTiposdePagamento && {
          title: "Tipos de Pagamento",
          icon: <FiFileText />,
          path: "TiposPagamento",
          onClick: () => handleNavigation("/TiposPagamento"),
        },
      ]
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title)),
    },
    {
      id: "configuracoes",
      title: "Configurações",
      icon: <FiSettings />,
      subMenus: [
        permiteConfiguracoesCampos && {
          title: "Configurações de Campo",
          icon: <FiGrid />,
          path: "ConfiguracoesCampos",
          onClick: () => handleNavigation("/ConfiguracoesCampos"),
        },
        permiteConfiguracoesEmail && {
          title: "Email para Notificações",
          icon: <FiMail />,
          path: "EmailNotificacao",
          onClick: () => handleNavigation("/EmailNotificacao"),
        },
        constPermiteGerarAgendamentos && {
          title: "Agendamentos",
          icon: <FiClock />,
          path: "Agendamentos",
          onClick: () => handleNavigation("/Agendamentos"),
        },
        constPermiteGruposUsuarios && {
          title: "Grupos de Usuários",
          icon: <FiUsers />,
          path: "GrupoUsuario",
          onClick: () => handleNavigation("/GrupoUsuario"),
        },
        permiteTiposAgendamento && {
          title: "Tipos de Agendamento",
          icon: <FiCalendar />,
          path: "TiposAgendamento",
          onClick: () => handleNavigation("/TiposAgendamento"),
        },
        constPermiteUsuarios && {
          title: "Usuários",
          icon: <FiUser />,
          path: "Usuario",
          onClick: () => handleNavigation("/Usuario"),
        },
        constPermiteCamposCustomizados && {
          title: "Campos Customizados",
          icon: <FiGrid />,
          path: "CamposCustomizados",
          onClick: () => handleNavigation("/CamposCustomizados"),
        },
      ]
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title)),
    },
  ];

  return (
    <>
      <div className="mobile-toggle">
        <button onClick={toggleMobileMenu} className="menu-toggle-btn">
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {mobileOpen && <div className="sidebar-overlay" onClick={toggleMobileMenu}></div>}

      <div className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <div
            className="logo-container"
            onClick={handleLogoClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleLogoClick();
              }
            }}
            aria-label={logoSrc ? "Abrir Estatisticas" : "Enviar logotipo da entidade"}
          >
            {!collapsed && (
              logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="sidebar-logo"
                />
              ) : (
                <span className="logo-placeholder">Enviar logo</span>
              )
            )}
          </div>
          <button className="toggle-btn" onClick={toggleSidebar} title={collapsed ? "Expandir menu" : "Minimizar menu"}>
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        <div className="sidebar-menu">
          {menuItems.map((item) => (
            <div key={item.id} className="menu-group">
              {item.subMenus ? (
                <>
                  <div
                    className={`menu-item ${activeMenu === item.id ? "active" : ""}`}
                    onClick={() => toggleSubMenu(item.id)}
                  >
                    <div className="menu-icon">{item.icon}</div>
                    {!collapsed && (
                      <>
                        <span className="menu-title">{item.title}</span>
                        <div className="menu-arrow">
                          {activeMenu === item.id ? <FiChevronDown /> : <FiChevronRight />}
                        </div>
                      </>
                    )}
                  </div>

                  {activeMenu === item.id && !collapsed && (
                    <div className="submenu">
                      {item.subMenus.map((subItem, index) => (
                        <div
                          key={index}
                          className={`submenu-item ${
                            isActive(subItem.path, subItem.classification) ? "active" : ""
                          }`}
                          onClick={subItem.onClick}
                          onMouseDown={(e) => {
                            if (e.button === 1) {
                              e.preventDefault();
                              window.open(`${window.location.origin}/${subItem.path}`, "_blank");
                            }
                          }}
                        >
                          <div className="submenu-icon">{subItem.icon}</div>
                          <span className="submenu-title">{subItem.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={`menu-item ${isActive(item.path) ? "active" : ""}`}
                  onClick={item.onClick}
                >
                  <div className="menu-icon">{item.icon}</div>
                  {!collapsed && <span className="menu-title">{item.title}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
        
      </div>

      <NovidadesVersaoModal
        show={showNovidadesModal}
        novidadesVersao={novidadesVersao}
        onClose={handleCloseNovidadesModal}
      />
      <MenuLogoUploadModal
        show={showLogoUploadModal}
        onHide={() => setShowLogoUploadModal(false)}
        idEntidade={idEntidade}
        onSaved={handleLogoSaved}
      />
    </>
  );
};

export default MenuSide;
